import React from "react";
import { Link } from "react-router-dom";
import { BookOpen, Clock, ArrowRight, Sparkles } from "lucide-react";
import { ARTICLES } from "../content/articles";
import { useSeo } from "../hooks/useSeo";

/**
 * Public content hub — /advice.
 *
 * Lists all articles as a keyword-rich grid. No auth. Fully indexable.
 * Every article card is a plain <Link> so Googlebot follows them without JS.
 */
export default function Advice() {
  useSeo({
    title: "Dating Advice for Singles Over 55 · Prime Connect",
    description:
      "Honest, practical dating advice for singles over 55 — verification, life after divorce, profile photos, and how the modern dating landscape actually works after 60.",
    canonical: "https://primeconnect55.com/advice",
    type: "website",
  });

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-20" data-testid="advice-hub">
      <div className="flex items-center gap-2 text-sm uppercase tracking-[0.28em] font-bold text-[#722f37]">
        <BookOpen className="w-4 h-4" /> Advice
      </div>
      <h1 className="mt-3 font-serif-display text-4xl md:text-5xl leading-tight" data-testid="advice-hub-heading">
        Advice for dating <br className="hidden md:block" />after 55.
      </h1>
      <p className="mt-4 text-lg text-[#5c5c5c] max-w-2xl">
        Field notes from members, moderators, and researchers about what actually works
        — no clichés, no algorithms selling you a course.
      </p>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="advice-hub-grid">
        {ARTICLES.map((a, i) => (
          <Link
            key={a.slug}
            to={`/advice/${a.slug}`}
            className="group flex flex-col justify-between rounded-2xl bg-white border border-[#e5e0d8] card-elev p-6 hover:border-[#722f37]/40 transition-colors"
            data-testid={`advice-card-${a.slug}`}
          >
            <div>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-[#722f37]">
                <Sparkles className="w-3.5 h-3.5 text-[#c39d2b]" />
                {a.cluster}
              </div>
              <h2 className="mt-3 font-serif-display text-2xl md:text-3xl text-[#1a1a1a] group-hover:text-[#722f37] transition-colors leading-snug">
                {a.title}
              </h2>
              <p className="mt-3 text-sm text-[#5c5c5c] leading-relaxed">
                {a.description}
              </p>
            </div>
            <div className="mt-6 flex items-center justify-between text-xs text-[#5c5c5c]">
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> {a.read_minutes} min read
              </span>
              <span className="inline-flex items-center gap-1 font-semibold text-[#722f37]">
                Read <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-14 rounded-2xl bg-[#faf9f6] border border-[#e5e0d8] p-6 md:p-10 text-center" data-testid="advice-hub-cta">
        <h3 className="font-serif-display text-3xl text-[#1a1a1a]">Ready to try Prime Connect?</h3>
        <p className="mt-3 text-[#5c5c5c] max-w-xl mx-auto">
          A tasteful, verified, rating-driven dating community for singles over 55. Free to sign up.
        </p>
        <Link
          to="/register"
          className="btn-primary mt-6 inline-flex items-center gap-2 !py-3 !px-6"
          data-testid="advice-hub-register"
        >
          Create a free account <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
